// The ceremony's fixed cast and the state shape shared by the Worker and the app.

export const PLAYER_IDS = ['p1', 'p2', 'p3', 'p4'] as const
export type PlayerId = (typeof PLAYER_IDS)[number]

export type Player = {
	id: PlayerId
	tag: string // P1
	short: string // VICTOR
	name: string // VICTOR WALLANDER
	company: string // WALL & BERG AB
	ink: string // the player's colour, drives borders, paths and fireworks
}

export const PLAYERS: Record<PlayerId, Player> = {
	p1: { id: 'p1', tag: 'P1', short: 'VICTOR', name: 'VICTOR WALLANDER', company: 'WALL & BERG AB', ink: '#ff6b2c' },
	p2: { id: 'p2', tag: 'P2', short: 'FREDRIK', name: 'FREDRIK AHRBERG', company: 'WALL & BERG AB', ink: '#2fc6ee' },
	p3: { id: 'p3', tag: 'P3', short: 'ZIAD', name: 'ZIAD SAWALHA', company: 'SAWALHA FAMILY AB', ink: '#e23ed2' },
	p4: { id: 'p4', tag: 'P4', short: 'JONAS', name: 'JONAS FROST', company: 'WEB FASTIGHETER AB', ink: '#72d63c' },
}

export const DOCUMENT_TITLE = 'GATE INTELLIGENCE AB // AKTIEÄGARAVTAL'

export type Stamp = { genTime: string; serial: string; token: string } // token: base64 DER

// How a player's identity got into their record. `declared` is typed by the player: a claim, not a proof.
export const ID_METHODS = {
	bankid: 'SWEDISH BANKID',
	freja: 'FREJA EID+',
	declared: 'SELF-DECLARED PERSONNUMMER',
} as const
export type IdMethod = keyof typeof ID_METHODS

// Who the eID provider says the person is. The personnummer never leaves the Worker in the
// clear: only a keyed fingerprint (HMAC with a server secret) is kept and stamped.
export type Verification = {
	method: IdMethod
	name: string // as the eID returned it, e.g. "Terne Paulsen"
	ssnHmac: string
	at: string
}

export type Signature = {
	player: PlayerId
	name: string
	company: string
	initials: string
	signedAt: string // ISO, server clock
	record: string // the exact JSON that was timestamped
	tsa: Stamp | null // null when DigiCert could not be reached; retried at the seal
}

export type TickerEvent = {
	at: string
	kind: 'signing' | 'verified' | 'signed' | 'complete'
	player?: PlayerId
	method?: IdMethod
	serial?: string // DigiCert serial of the stamp this event produced
}

export type CeremonyState = {
	docSha256: string
	signatures: Partial<Record<PlayerId, Signature>>
	verified: Partial<Record<PlayerId, Verification>>
	signing: PlayerId[] // everyone with their signing screen open right now
	completedAt: string | null
	seal: Stamp | null // DigiCert stamp over the manifest of all four
	events: TickerEvent[] // newest last
}

export type Role = PlayerId | 'board' | 'admin'

export function signedCount(s: CeremonyState): number {
	return PLAYER_IDS.filter(id => s.signatures[id]).length
}

export function initialState(docSha256: string): CeremonyState {
	return { docSha256, signatures: {}, verified: {}, signing: [], completedAt: null, seal: null, events: [] }
}

// What gets timestamped for one signature. Key order is fixed so the bytes are reproducible.
// With an eID verification, who the eID says signed goes into the stamped bytes too.
export function signatureRecord(
	docSha256: string,
	p: Player,
	initials: string,
	signedAt: string,
	v?: Verification,
): string {
	return JSON.stringify({
		document: DOCUMENT_TITLE,
		docSha256,
		player: p.id,
		name: p.name,
		company: p.company,
		initials,
		signedAt,
		...(v && { verifiedBy: ID_METHODS[v.method], verifiedName: v.name, ssnHmac: v.ssnHmac, verifiedAt: v.at }),
	})
}

// What gets timestamped when the fourth signature lands: every record, in player order.
export function manifest(s: CeremonyState): string {
	return JSON.stringify({
		document: DOCUMENT_TITLE,
		docSha256: s.docSha256,
		signatures: PLAYER_IDS.map(id => s.signatures[id]?.record ?? null),
	})
}

export const INITIALS_RE = /^[A-ZÅÄÖ]{2,3}$/
